// HourGridSchedule.tsx
import React, { useState } from "react";
import { Box, Container, Typography, TextField, Paper } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

interface Reservation {
  id: number;
  court: string;
  startTime: string; // Formato "HH:mm"
  endTime: string; // Formato "HH:mm"
  user: string;
}

const courts = ["Cancha 1", "Cancha 2", "Cancha 3"];
const startHour = 8;
const endHour = 22;
const hourHeight = 60; // Cada hora ocupará 60px
const containerHeight = (endHour - startHour) * hourHeight;
const headerHeight = 40; // Altura del encabezado para los nombres de canchas

const dummyReservations: Reservation[] = [
  {
    id: 1,
    court: "Cancha 1",
    startTime: "10:00",
    endTime: "11:30",
    user: "Juan",
  },
  {
    id: 2,
    court: "Cancha 2",
    startTime: "11:30",
    endTime: "13:00",
    user: "María",
  },
  {
    id: 3,
    court: "Cancha 3",
    startTime: "09:30",
    endTime: "11:30",
    user: "Carlos",
  },
];

const parseTime = (timeStr: string) => {
  const [hour, minute] = timeStr.split(":").map(Number);
  return { hour, minute };
};

const HourGridSchedule: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const handleDateChange = (date: Date | null) => setSelectedDate(date);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container sx={{ my: 2 }}>
        {/* Selector de fecha */}
        <Box mb={2}>
          <DesktopDatePicker
            label="Seleccionar fecha"
            value={selectedDate}
            onChange={handleDateChange}
            format="dd/MM/yyyy"
            slots={{ textField: TextField }}
            slotProps={{ textField: { variant: "outlined" } }}
          />
        </Box>
        <Paper elevation={3}>
          {/* Encabezado: fila con títulos de las canchas */}
          <Box sx={{ display: "flex", borderBottom: "1px solid #ccc" }}>
            {/* Columna de la izquierda vacía */}
            <Box sx={{ width: 80, borderRight: "1px solid #ccc" }} />
            {courts.map((court) => (
              <Box
                key={court}
                sx={{
                  flex: 1,
                  borderRight: "1px solid #ccc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: "bold",
                  height: headerHeight,
                }}
              >
                {court}
              </Box>
            ))}
          </Box>

          {/* Cuerpo del calendario */}
          <Box sx={{ display: "flex" }}>
            {/* Columna de horarios */}
            <Box sx={{ width: 80, borderRight: "1px solid #ccc" }}>
              {Array.from({ length: endHour - startHour }).map((_, idx) => {
                const hour = startHour + idx;
                return (
                  <Box
                    key={hour}
                    sx={{
                      height: hourHeight,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      pr: 1,
                      borderBottom: "1px solid #eee",
                    }}
                  >
                    <Typography variant="body2">{hour}:00</Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Columnas para cada cancha */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                position: "relative",
                height: containerHeight,
              }}
            >
              {courts.map((court) => {
                const courtReservations = dummyReservations.filter(
                  (r) => r.court === court
                );
                return (
                  <Box
                    key={court}
                    sx={{
                      flex: 1,
                      borderRight: "1px solid #ccc",
                      position: "relative",
                      height: containerHeight,
                      overflow: "hidden",
                      // Fondo con cuadrícula horizontal (una línea cada hora)
                      backgroundImage: `repeating-linear-gradient(
                        to bottom,
                        transparent,
                        transparent ${hourHeight - 1}px,
                        #ccc ${hourHeight - 1}px,
                        #ccc ${hourHeight}px
                      )`,
                    }}
                  >
                    {courtReservations.map((reservation) => {
                      const { hour: startH, minute: startM } = parseTime(
                        reservation.startTime
                      );
                      const { hour: endH, minute: endM } = parseTime(
                        reservation.endTime
                      );
                      const startTotal = startH * 60 + startM;
                      const containerStart = startHour * 60;
                      const topOffset = startTotal - containerStart;
                      const endTotal = endH * 60 + endM;
                      const duration = endTotal - startTotal;
                      return (
                        <Box
                          key={reservation.id}
                          sx={{
                            position: "absolute",
                            top: topOffset + 2,
                            left: 4,
                            right: 4,
                            height: duration - 4,
                            backgroundColor: "primary.main", // Usa el color primary del theme
                            color: "white",
                            borderRadius: 1,
                            p: 0.5,
                            fontSize: "0.7rem",
                            overflow: "hidden",
                            zIndex: 2,
                          }}
                        >
                          <Typography variant="caption">
                            {reservation.startTime} - {reservation.endTime}
                          </Typography>
                          <Typography variant="body2">
                            {reservation.user}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Paper>
      </Container>
    </LocalizationProvider>
  );
};

export default HourGridSchedule;
